/**
 * TeamSelector - Sport / League / Team onboarding flow
 *
 * 3-step selector: Sport → League → Team
 * OLED black, minimal, thin typeface — matches platform design language.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, Users, Check, Loader2, ChevronRight } from 'lucide-react';
import { useTeam } from '../lib/teamConfig';
import {
  inferGenderFromLeagueCode,
  normalizeSelectedTeamSelection,
} from '../lib/chat/sportContext';
import {
  NATSTAT_SERVICES,
  fetchNatStatTeams,
} from '../utils/natstatDataService';

// ─── Sport config ─────────────────────────────────────────────────────────────
// Emoji icons — simple, no SVG, consistent with sidebar style
// comingSoon: true → show grayed card, block navigation (no route exists yet)
const SPORT_CONFIG = {
  basketball: {
    id: 'basketball',
    name: 'Basketball',
    icon: '🏀',
    leagues: NATSTAT_SERVICES.Basketball || [],
    routePrefix: '/basketball',
  },
  baseball: {
    id: 'baseball',
    name: 'Baseball',
    icon: '⚾',
    leagues: NATSTAT_SERVICES.Baseball || [],
    routePrefix: '/baseball',
  },
  hockey: {
    id: 'hockey',
    name: 'Hockey',
    icon: '🏒',
    leagues: NATSTAT_SERVICES.Hockey || [],
    routePrefix: '/hockey',
  },
  football: {
    id: 'football',
    name: 'Football',
    icon: '🏈',
    leagues: NATSTAT_SERVICES.Football || [],
    routePrefix: '/football',
  },
  soccer: {
    id: 'soccer',
    name: 'Soccer',
    icon: '⚽',
    leagues: NATSTAT_SERVICES.Soccer || [],
    routePrefix: '/soccer',
  },
  golf: {
    id: 'golf',
    name: 'Golf',
    icon: '⛳',
    leagues: NATSTAT_SERVICES.Golf || [],
    routePrefix: '/golf',
  },
  f1: {
    id: 'f1',
    name: 'Formula 1',
    icon: '🏎️',
    leagues: [{ code: 'F1', name: 'Formula 1', level: 'pro' }],
    routePrefix: '/f1',
  },
};

// Group leagues by tier
const groupLeaguesByLevel = (leagues) => {
  const groups = {
    Professional:  leagues.filter((l) => l.level === 'pro'),
    'Minor League': leagues.filter((l) => l.level === 'minor'),
    'College D1':  leagues.filter((l) => l.level === 'college_d1' || l.level === 'college'),
    'College D2/D3': leagues.filter((l) => l.level === 'college_d2' || l.level === 'college_d3'),
    Other:         leagues.filter((l) => ['naia', 'juco', 'intl'].includes(l.level)),
  };
  return Object.fromEntries(Object.entries(groups).filter(([, v]) => v.length > 0));
};

// ─── Shared design tokens ─────────────────────────────────────────────────────
const T = {
  border:  '#1A1A1A',
  surface: '#0A0A0A',
  t1:      '#F5F5F5',
  t2:      '#888888',
  t3:      '#444444',
  gold:    '#E8B800',
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function StepIndicator({ step }) {
  const LABELS = ['Sport', 'League', 'Team'];
  return (
    <div className="flex items-center justify-center gap-0 mb-12">
      {[1, 2, 3].map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex flex-col items-center gap-1.5">
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
                background:
                  s < step  ? T.gold :
                  s === step ? T.gold :
                  T.surface,
                border: `1px solid ${
                  s <= step ? T.gold : T.border
                }`,
                color: s <= step ? '#000' : T.t3,
                transition: 'all 0.2s',
              }}
            >
              {s < step ? <Check size={12} strokeWidth={2.5} /> : s}
            </div>
            <span
              style={{
                fontSize: 9,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: s <= step ? T.gold : T.t3,
                fontWeight: s === step ? 600 : 400,
              }}
            >
              {LABELS[i]}
            </span>
          </div>
          {i < 2 && (
            <div
              style={{
                width: 48,
                height: 1,
                marginBottom: 16,
                background: s < step ? T.gold : T.border,
                transition: 'background 0.2s',
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function TeamSelector() {
  const navigate = useNavigate();
  const { setSelectedTeam } = useTeam();

  const [step, setStep] = useState(1);
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedTeamData, setSelectedTeamData] = useState(null);

  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const sportConfig = selectedSport ? SPORT_CONFIG[selectedSport] : null;

  const groupedLeagues = useMemo(() => {
    if (!sportConfig) return {};
    return groupLeaguesByLevel(sportConfig.leagues);
  }, [sportConfig]);

  const filteredTeams = useMemo(() => {
    if (!searchQuery) return teams;
    const q = searchQuery.toLowerCase();
    return teams.filter(
      (t) =>
        t.team_name?.toLowerCase().includes(q) ||
        t.full_name?.toLowerCase().includes(q) ||
        t.conference?.toLowerCase().includes(q)
    );
  }, [teams, searchQuery]);

  const groupedTeams = useMemo(() => {
    const g = {};
    filteredTeams.forEach((t) => {
      const conf = t.conference || 'Other';
      if (!g[conf]) g[conf] = [];
      g[conf].push(t);
    });
    return Object.fromEntries(
      Object.entries(g).sort(([a], [b]) => {
        if (a === 'Other') return 1;
        if (b === 'Other') return -1;
        return a.localeCompare(b);
      })
    );
  }, [filteredTeams]);

  const buildTeamSelection = useCallback(
    (team) => {
      const leagueCode = selectedLeague?.code || null;
      const gender = inferGenderFromLeagueCode(leagueCode);
      return normalizeSelectedTeamSelection({
        sport: selectedSport,
        sportCategory: selectedSport,
        sportCode: leagueCode,
        leagueCode,
        leagueName: selectedLeague?.name || null,
        leagueLevel: selectedLeague?.level || null,
        gender,
        league: selectedLeague,
        team,
        teamId: team?.team_id || team?.id || team?.team_code || null,
        teamName: team?.team_name || team?.full_name || team?.name || null,
        teamAbbrev: team?.team_code || team?.abbreviation || team?.abbrev || null,
        conference: team?.conference || null,
        selectedAt: new Date().toISOString(),
      });
    },
    [selectedLeague, selectedSport]
  );

  const persistLegacySelection = useCallback((teamSelection) => {
    if (typeof window === 'undefined' || !teamSelection) return;
    const legacySport = teamSelection.league || {
      code: teamSelection.leagueCode || teamSelection.sportCode || null,
      name: teamSelection.leagueName || teamSelection.sportCategory || null,
      level: teamSelection.leagueLevel || null,
      gender: teamSelection.gender || null,
    };
    sessionStorage.setItem('selectedSport', JSON.stringify(legacySport));
    sessionStorage.setItem('selectedTeam', JSON.stringify(teamSelection.team || {}));
    window.dispatchEvent(
      new CustomEvent('teamChange', {
        detail: { sport: legacySport, team: teamSelection.team || null, selection: teamSelection },
      })
    );
  }, []);

  useEffect(() => {
    if (selectedLeague) {
      setTeamsLoading(true);
      setTeams([]);
      setSearchQuery('');
      fetchNatStatTeams(selectedLeague.code)
        .then((data) => setTeams(data || []))
        .catch(() => setTeams([]))
        .finally(() => setTeamsLoading(false));
    }
  }, [selectedLeague]);

  const handleSportSelect = useCallback((sportId) => {
    // Block navigation to sports that don't have routes yet
    if (SPORT_CONFIG[sportId]?.comingSoon) return;
    setSelectedSport(sportId);
    setSelectedLeague(null);
    setSelectedTeamData(null);
    setTeams([]);
    setStep(2);
  }, []);

  const handleLeagueSelect = useCallback((league) => {
    setSelectedLeague(league);
    setSelectedTeamData(null);
    setStep(3);
  }, []);

  const handleTeamSelect = useCallback(
    (team) => {
      setSelectedTeamData(team);
      const teamSelection = buildTeamSelection(team);
      setSelectedTeam(teamSelection);
      persistLegacySelection(teamSelection);
      const routePrefix = SPORT_CONFIG[selectedSport]?.routePrefix || '/baseball';
      navigate(`${routePrefix}/canvas`);
    },
    [buildTeamSelection, selectedSport, setSelectedTeam, persistLegacySelection, navigate]
  );

  const handleBack = useCallback(() => {
    if (step === 2) { setSelectedSport(null); setStep(1); }
    else if (step === 3) { setSelectedLeague(null); setSelectedTeamData(null); setTeams([]); setStep(2); }
  }, [step]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '48px 24px 80px',
      }}
    >
      {/* Logo */}
      <div style={{ marginBottom: 40, textAlign: 'center' }}>
        <img
          src="/SLAM_logo.png"
          alt="SLAM Sports AI"
          style={{ height: 36, width: 'auto', margin: '0 auto 20px', display: 'block', opacity: 0.9 }}
        />
        <h1
          style={{
            fontSize: step === 1 ? 22 : 18,
            fontWeight: 300,
            color: T.t1,
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          {step === 1 && 'Choose your sport'}
          {step === 2 && `${sportConfig?.name} — select a league`}
          {step === 3 && `${selectedLeague?.name} — select your team`}
        </h1>
        <p style={{ fontSize: 12, color: T.t3, marginTop: 6, fontWeight: 400 }}>
          {step === 1 && 'Choose your sport to get started'}
          {step === 2 && `${sportConfig?.leagues.length} leagues available`}
          {step === 3 && `${teams.length} teams`}
        </p>
      </div>

      <StepIndicator step={step} />

      {/* Back */}
      {step > 1 && (
        <button
          onClick={handleBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 32,
            fontSize: 12,
            color: T.t3,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 0',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = T.t1)}
          onMouseLeave={(e) => (e.currentTarget.style.color = T.t3)}
        >
          <ChevronLeft size={14} />
          Back
        </button>
      )}

      {/* ── Step 1: Sport grid ──────────────────────────────────────────────── */}
      {step === 1 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12,
            width: '100%',
            maxWidth: 680,
          }}
        >
          {Object.values(SPORT_CONFIG).map((sport) => (
            <SportCard key={sport.id} sport={sport} onClick={() => handleSportSelect(sport.id)} />
          ))}
        </div>
      )}

      {/* ── Step 2: League list ─────────────────────────────────────────────── */}
      {step === 2 && sportConfig && (
        <div style={{ width: '100%', maxWidth: 560 }}>
          {Object.entries(groupedLeagues).map(([level, leagues]) => (
            <div key={level} style={{ marginBottom: 28 }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: T.t3,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                  paddingLeft: 4,
                }}
              >
                {level}
              </div>
              <div
                style={{
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                {leagues.map((league, i) => (
                  <LeagueRow
                    key={league.code}
                    league={league}
                    isLast={i === leagues.length - 1}
                    onClick={() => handleLeagueSelect(league)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Step 3: Team list ───────────────────────────────────────────────── */}
      {step === 3 && selectedLeague && (
        <div style={{ width: '100%', maxWidth: 560 }}>
          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 24 }}>
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                color: T.t3,
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              placeholder="Start typing to find your team…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                paddingLeft: 38,
                paddingRight: 16,
                paddingTop: 10,
                paddingBottom: 10,
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                color: T.t1,
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.target.style.borderColor = 'rgba(232,184,0,0.4)')}
              onBlur={(e) => (e.target.style.borderColor = T.border)}
            />
          </div>

          {teamsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', padding: '48px 0', color: T.t3 }}>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 13 }}>Loading teams…</span>
            </div>
          ) : Object.keys(groupedTeams).length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
              {Object.entries(groupedTeams).map(([conference, confTeams]) => (
                <div key={conference}>
                  {/* Conference / Division header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 10,
                      paddingLeft: 2,
                    }}
                  >
                    {/* Gold accent bar */}
                    <div style={{ width: 3, height: 16, background: T.gold, borderRadius: 2, flexShrink: 0 }} />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#AAAAAA',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        flex: 1,
                      }}
                    >
                      {conference}
                    </span>
                    {/* Team count badge */}
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: T.t3,
                        background: '#111',
                        border: `1px solid ${T.border}`,
                        borderRadius: 20,
                        padding: '1px 8px',
                      }}
                    >
                      {confTeams.length}
                    </span>
                  </div>
                  <div
                    style={{
                      border: `1px solid ${T.border}`,
                      borderRadius: 12,
                      overflow: 'hidden',
                    }}
                  >
                    {confTeams.map((team, i) => (
                      <TeamRow
                        key={team.team_id}
                        team={team}
                        isSelected={selectedTeamData?.team_id === team.team_id}
                        isLast={i === confTeams.length - 1}
                        onClick={() => handleTeamSelect(team)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px 0', color: T.t3 }}>
              <Users size={28} style={{ margin: '0 auto 10px', opacity: 0.3 }} />
              <p style={{ fontSize: 13 }}>
                {searchQuery ? 'No teams match your search' : 'No teams available'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sport card ───────────────────────────────────────────────────────────────

function SportCard({ sport, onClick }) {
  const [hovered, setHovered] = useState(false);
  const disabled = !!sport.comingSoon;
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: disabled ? '#000' : hovered ? '#0A0A0A' : '#000',
        border: `1px solid ${disabled ? '#141414' : hovered ? 'rgba(232,184,0,0.35)' : T.border}`,
        borderRadius: 14,
        padding: '28px 20px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
        textAlign: 'left',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {/* Coming Soon badge */}
      {disabled && (
        <span
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            fontSize: 8,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: T.t3,
            background: '#111',
            border: `1px solid ${T.border}`,
            borderRadius: 4,
            padding: '2px 5px',
          }}
        >
          Soon
        </span>
      )}

      {/* Icon */}
      <span style={{ fontSize: 22, lineHeight: 1, marginBottom: 14 }}>{sport.icon}</span>

      {/* Name */}
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: disabled ? T.t3 : hovered ? T.t1 : '#CCCCCC',
          letterSpacing: '-0.01em',
          marginBottom: 4,
          transition: 'color 0.15s',
        }}
      >
        {sport.name}
      </span>

      {/* League count */}
      <span style={{ fontSize: 11, color: T.t3, fontWeight: 400 }}>
        {sport.leagues.length} league{sport.leagues.length !== 1 ? 's' : ''}
      </span>
    </button>
  );
}

// ─── League row ───────────────────────────────────────────────────────────────

function LeagueRow({ league, isLast, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        background: hovered ? '#0A0A0A' : 'transparent',
        border: 'none',
        borderBottom: isLast ? 'none' : `1px solid ${T.border}`,
        cursor: 'pointer',
        transition: 'background 0.1s',
        textAlign: 'left',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: 10,
            fontWeight: 600,
            color: hovered ? T.gold : T.t3,
            letterSpacing: '0.05em',
            minWidth: 36,
            transition: 'color 0.1s',
          }}
        >
          {league.code}
        </span>
        <span style={{ fontSize: 13, color: hovered ? T.t1 : '#BBBBBB', fontWeight: 400, transition: 'color 0.1s' }}>
          {league.name}
        </span>
      </div>
      <ChevronRight size={13} style={{ color: hovered ? T.gold : T.t3, flexShrink: 0, transition: 'color 0.1s' }} />
    </button>
  );
}

// ─── Team row ─────────────────────────────────────────────────────────────────

function TeamRow({ team, isSelected, isLast, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '11px 16px',
        background: isSelected ? 'rgba(232,184,0,0.07)' : hovered ? '#0A0A0A' : 'transparent',
        border: 'none',
        borderBottom: isLast ? 'none' : `1px solid ${T.border}`,
        cursor: 'pointer',
        transition: 'background 0.1s',
        textAlign: 'left',
      }}
    >
      <div>
        <span
          style={{
            fontSize: 13,
            fontWeight: isSelected ? 500 : 400,
            color: isSelected ? T.gold : hovered ? T.t1 : '#BBBBBB',
            transition: 'color 0.1s',
          }}
        >
          {team.team_name}
        </span>
        {team.full_name && team.full_name !== team.team_name && (
          <span style={{ fontSize: 11, color: T.t3, marginLeft: 8 }}>{team.full_name}</span>
        )}
      </div>
      {isSelected && <Check size={13} style={{ color: T.gold, flexShrink: 0 }} />}
    </button>
  );
}
